import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A surface's re-read, offered from the shell.
 *
 * Re-reading is a SYNC-shaped act — "go and look again" — so it belongs beside
 * the sync state rather than among the controls that decide how a board is
 * laid out. It was looked for there and not found. Only the route knows how to
 * re-read itself, though, so the action comes from the route and the button
 * lives in the bar, the same seam the override bar uses.
 */
const RereadContext = createContext<{
  reread: (() => void) | null;
  setReread: (reread: (() => void) | null) => void;
}>({ reread: null, setReread: () => {} });

export function RereadProvider({ children }: { children: ReactNode }) {
  const [reread, setReread] = useState<(() => void) | null>(null);
  const value = useMemo(() => ({ reread, setReread }), [reread]);
  return <RereadContext.Provider value={value}>{children}</RereadContext.Provider>;
}

export function useReread(): (() => void) | null {
  return useContext(RereadContext).reread;
}

/** Offers a re-read while this surface is mounted, and withdraws it on the way
 *  out — a stale one would re-read a page nobody is looking at. */
export function useProvideReread(reread: () => void): void {
  const { setReread } = useContext(RereadContext);
  useEffect(() => {
    // Stored behind a setter call so React does not mistake the function for
    // a lazy initialiser and invoke it.
    setReread(() => reread);
    return () => setReread(null);
  }, [setReread, reread]);
}
